import { Event, Listing, Source } from '@/models/graph';
import { environment } from 'src/environments/environment';
import { PUNKSMARKET_V1_ROUTER_ADDRESS } from '@/constants/punksmarket-v1';

/** Contracts queried for dashboard bought/sold sections (alternative indexer). */
export function buildDashboardSaleCollections(): string[] {
  const collections = [environment.wrappedPunksAddress];

  if (environment.collection === 'cryptopunks') {
    collections.push(environment.punksMarketAddress);
    if (
      environment.c721Address &&
      environment.c721Address !== '0x0000000000000000000000000000000000000000'
    ) {
      collections.push(environment.c721Address);
    }
  } else if (environment.collection === 'cryptopunks-v1') {
    collections.push(environment.punksMarketAddress);
    if (environment.v1PunksMarketAddress) {
      collections.push(environment.v1PunksMarketAddress);
    }
  }

  return collections;
}

/**
 * Dashboard grid background: purple (wrapped) vs green (native/unwrapped).
 * For V1, use indexed `collection` — WPV1 ERC721 sales are wrapped regardless of platform
 * (including fallback `ethereum` from erc721-sales when no marketplace parser matches).
 */
export function isDashboardSaleWrapped(sale: {
  platform?: string;
  collection?: string;
  wrapped?: boolean;
}): boolean {
  if (environment.collection === 'cryptophunks') {
    return false;
  }

  if (environment.collection === 'cryptopunks-v1') {
    if (sale.wrapped === true) {
      return true;
    }
    const wrappedAddr = environment.wrappedPunksAddress?.toLowerCase();
    return !!wrappedAddr && sale.collection?.toLowerCase() === wrappedAddr;
  }

  const platform = sale.platform ?? '';
  return platform === 'blur' || platform === 'opensea' || platform === 'punkv1';
}

/** Owned grid background: purple (WPV1) vs green (native V1). */
export type IndexerListedToken = {
  tokenId: string;
  collection?: string;
  offeredDate?: string;
  amount?: number | string;
  platform?: string;
};

const NATIVE_LISTING_PLATFORMS = new Set([
  'punksv1',
  'punksmarket',
  'punkv1',
  'cryptopunks',
  'punksmarketplace',
]);

/** Native Larva / PunksMarket listing (green bg), not an external marketplace ask. */
export function isDashboardNativeListing(
  collection?: string,
  platform?: string,
): boolean {
  const normalizedPlatform = (platform ?? '').toLowerCase();
  if (normalizedPlatform && NATIVE_LISTING_PLATFORMS.has(normalizedPlatform)) {
    return true;
  }

  const normalizedCollection = collection?.toLowerCase() || '';
  const v1Native = environment.v1PunksMarketAddress?.toLowerCase();
  return !!v1Native && normalizedCollection === v1Native;
}

export function getDashboardListingSource(
  platform: string | undefined,
  tokenId: string,
  collection?: string,
): Source | undefined {
  if (isDashboardNativeListing(collection, platform)) {
    return undefined;
  }

  const contract = environment.wrappedPunksAddress?.toLowerCase();
  const normalizedPlatform = (platform ?? '').toLowerCase();

  if (normalizedPlatform === 'blurio' || normalizedPlatform === 'blur') {
    return {
      id: 'blur',
      domain: 'blur.io',
      name: 'Blur',
      icon: 'https://blur.io/favicons/180.png',
      url: `https://blur.io/asset/${contract}/${tokenId}`,
      internal: null,
    };
  }

  if (normalizedPlatform === 'opensea') {
    return {
      id: 'opensea',
      domain: 'opensea.io',
      name: 'OpenSea',
      icon: '/images/opensea-logo.svg',
      url: `https://opensea.io/assets/ethereum/${contract}/${tokenId}`,
      internal: null,
    };
  }

  return undefined;
}

/** Listing row from listing-indexer (`listingsByCollection`). */
export type MarketplaceIndexerListing = {
  tokenId: string;
  priceAmount?: string | number;
  currentOwner?: string;
  platform?: string;
  listedAt?: string;
};

export function mapMarketplaceIndexerListingToListing(
  listing: MarketplaceIndexerListing,
  ethToWei: (eth: string) => string,
): Listing | null {
  if (!listing.tokenId) {
    return null;
  }

  const source = getDashboardListingSource(listing.platform, listing.tokenId);
  if (!source?.icon) {
    return null;
  }

  return {
    id: listing.tokenId,
    value: ethToWei(String(listing.priceAmount ?? '0')),
    blockTimestamp: listing.listedAt,
    source,
    nativePunksmarket: false,
  };
}

export function mapIndexerListedTokenToListing(
  listedToken: IndexerListedToken,
  ethToWei: (eth: string) => string,
): Listing {
  const collection = listedToken.collection?.toLowerCase();
  const nativePunksmarket = isDashboardNativeListing(collection, listedToken.platform);
  const source = getDashboardListingSource(
    listedToken.platform,
    listedToken.tokenId,
    collection,
  );

  return {
    id: listedToken.tokenId,
    value: listedToken.amount ? ethToWei(String(listedToken.amount)) : '0',
    blockTimestamp: listedToken.offeredDate,
    nativePunksmarket,
    source,
    ...(nativePunksmarket
      ? { toAccount: { id: PUNKSMARKET_V1_ROUTER_ADDRESS, punks: [] } }
      : {}),
  };
}

/** Prefer native Larva / PunksMarket listing over external marketplace asks. */
export function mergeOwnedListings(
  ...candidates: (Listing | null | undefined)[]
): Listing | null {
  const valid = candidates.filter(
    (listing): listing is Listing => !!listing?.value && listing.value !== '0',
  );

  if (!valid.length) {
    return null;
  }

  const native = valid.find((listing) => listing.nativePunksmarket);
  if (native) {
    return native;
  }

  return valid.reduce((best, current) => {
    if (!best) {
      return current;
    }

    const bestHasSource = !!best.source?.icon;
    const currentHasSource = !!current.source?.icon;
    if (currentHasSource && !bestHasSource) {
      return current;
    }
    if (bestHasSource && !currentHasSource) {
      return best;
    }

    try {
      return BigInt(current.value) < BigInt(best.value) ? current : best;
    } catch {
      return current.value < best.value ? current : best;
    }
  });
}

export function isOwnedPunkWrapped(punk: {
  wrapped?: boolean;
  c721wrapped?: boolean;
  collection?: string;
}): boolean {
  if (punk.c721wrapped) {
    return true;
  }

  if (environment.collection === 'cryptophunks') {
    return false;
  }

  if (environment.collection === 'cryptopunks-v1') {
    if (punk.wrapped === true) {
      return true;
    }
    const wrappedAddr = environment.wrappedPunksAddress?.toLowerCase();
    return !!wrappedAddr && punk.collection?.toLowerCase() === wrappedAddr;
  }

  return punk.wrapped === true;
}

/** Full local date/time for dashboard sale tooltips. */
export function formatDashboardSaleDate(blockTimestamp?: string | number | null): string {
  if (blockTimestamp == null || blockTimestamp === '') {
    return '—';
  }

  const timestamp = Number(blockTimestamp);
  if (!Number.isFinite(timestamp)) {
    return '—';
  }

  const ms = String(Math.floor(timestamp)).length === 10 ? timestamp * 1000 : timestamp;
  const date = new Date(ms);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString();
}

export function buildDashboardSaleDateTippyOptions(blockTimestamp?: string | number | null) {
  return {
    content: formatDashboardSaleDate(blockTimestamp),
    placement: 'top' as const,
    theme: 'punks',
  };
}

export function withDashboardSaleDateTippyOptions<T extends { blockTimestamp?: string | number | null }>(
  events: T[],
): Array<T & { saleDateTippyOptions: ReturnType<typeof buildDashboardSaleDateTippyOptions> }> {
  return events.map((event) => ({
    ...event,
    saleDateTippyOptions: buildDashboardSaleDateTippyOptions(event.blockTimestamp),
  }));
}

export type DashboardSaleEvent = Event & {
  saleDateTippyOptions: ReturnType<typeof buildDashboardSaleDateTippyOptions>;
};
